import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMe } from '@app/api/hooks/shell';
import { onSessionExpired, type SessionExpiredDetail } from '@app/api/sessionExpired';
import { SessionContext, type StateSession, type SessionValue } from './sessionContext';

export interface SessionProviderProps {
  children: ReactNode;
}

/**
 * Sessão do console (FR-006, FR-007, FR-009).
 *
 * Fonte única: `getMe`. Um 401 em **qualquer** chamada (não só em `getMe`) dispara
 * o evento global `session-expired` do cliente HTTP; aqui ele marca a sessão como
 * anônima na hora, sem refazer `getMe` — o que só devolveria outro 401 e outro
 * evento. `RequireAuth` faz o redirect para T1 preservando a rota de origem.
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const { data, error, isSuccess, refetch } = useMe();
  // Guardamos o **código** do evento, não um booleano: `nao_autenticado` também
  // encerra a sessão, mas só `sessao_expirada` autoriza dizer "sua sessão expirou".
  const [codigoDoEvento, setCodigoDoEvento] = useState<SessionExpiredDetail['code'] | null>(null);

  useEffect(() => onSessionExpired((detalhe) => setCodigoDoEvento(detalhe.code)), []);

  // Um `getMe` bem-sucedido é o fim da expiração (voltou de T1, por exemplo).
  useEffect(() => {
    if (isSuccess) setCodigoDoEvento(null);
  }, [isSuccess]);

  const recarregar = useCallback(() => {
    setCodigoDoEvento(null);
    void refetch();
  }, [refetch]);

  // Saída deliberada: a sessão acabou, mas não "expirou" — nada de T1 dizendo isso.
  const shutdown = useCallback(() => setCodigoDoEvento('nao_autenticado'), []);

  const value = useMemo<SessionValue>(() => {
    const erro = error ?? null;
    const anonimo = codigoDoEvento !== null || Boolean(erro?.isSessionExpired);

    const estado: StateSession = anonimo
      ? 'anonimo'
      : data
        ? 'autenticado'
        : erro
          ? 'indisponivel'
          : 'carregando';

    return {
      estado,
      // `sessao_expirada` e o evento global vêm de sessão que existia; um
      // `nao_autenticado` seco é só "nunca entrou".
      expirada:
        anonimo && (codigoDoEvento === 'sessao_expirada' || erro?.code === 'sessao_expirada'),
      usuario: estado === 'autenticado' ? (data?.user ?? null) : null,
      papel: estado === 'autenticado' ? (data?.user.role ?? null) : null,
      tenant: estado === 'autenticado' ? (data?.tenant ?? null) : null,
      googleLoginConfigurado: data?.google_login_configured ?? false,
      erro,
      recarregar,
      encerrar: shutdown,
    };
  }, [data, error, codigoDoEvento, recarregar, shutdown]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
